import React, { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useLoginMutation } from '../features/api/authEndpoints'
import { setCredentials } from '../store/authSlice'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [login, { isLoading }] = useLoginMutation()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      enqueueSnackbar('Email and password are required', { variant: 'warning' })
      return
    }
    try {
      const data = await login({ email: email.trim(), password }).unwrap()
      dispatch(setCredentials({ token: data.token, admin: data.admin }))
      enqueueSnackbar('Logged in successfully', { variant: 'success' })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      const message = err?.data?.error || err?.message || 'Login failed'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  return (
    <Box
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark'
        return {
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          background: isDark
            ? 'radial-gradient(circle at 20% 12%, rgba(91,63,214,0.34), transparent 32%), radial-gradient(circle at 80% 0%, rgba(240,198,106,0.2), transparent 30%), linear-gradient(135deg, #070912, #17101f)'
            : 'radial-gradient(circle at 20% 12%, rgba(240,198,106,0.34), transparent 32%), radial-gradient(circle at 80% 0%, rgba(91,63,214,0.12), transparent 30%), linear-gradient(135deg, #f9f0dc, #fffaf0)',
          px: 2,
        }
      }}
    >
      <Paper
        elevation={0}
        sx={(theme) => {
          const isDark = theme.palette.mode === 'dark'
          return {
            position: 'relative',
            overflow: 'hidden',
            p: 4,
            maxWidth: 430,
            width: '100%',
            border: isDark ? '1px solid rgba(240,198,106,0.26)' : '1px solid rgba(91,63,214,0.14)',
            borderRadius: 4,
            bgcolor: 'background.paper',
            background: isDark
              ? 'linear-gradient(145deg, rgba(24,18,38,0.96), rgba(10,12,22,0.96)), radial-gradient(circle at 50% 0%, rgba(240,198,106,0.18), transparent 34%)'
              : 'linear-gradient(145deg, rgba(255,250,240,0.98), rgba(249,240,220,0.96)), radial-gradient(circle at 50% 0%, rgba(91,63,214,0.1), transparent 34%)',
            boxShadow: isDark
              ? '0 28px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)'
              : '0 28px 70px rgba(73,47,25,0.14), inset 0 1px 0 rgba(255,255,255,0.86)',
            '&::before': {
              content: '"♠"',
              position: 'absolute',
              right: 24,
              top: 10,
              color: isDark ? 'rgba(240,198,106,0.12)' : 'rgba(91,63,214,0.1)',
              fontSize: 82,
              lineHeight: 1,
            },
          }
        }}
        component="form"
        onSubmit={handleSubmit}
        aria-label="Admin login form"
      >
        <Typography variant="overline" display="block" align="center" color="secondary.main" sx={{ fontWeight: 900, letterSpacing: 2 }}>
          Royal Flush Elite
        </Typography>
        <Typography variant="h5" gutterBottom align="center" sx={{ fontWeight: 900 }}>
          Poker Admin Login
        </Typography>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          autoComplete="email"
          required
          inputProps={{ 'aria-label': 'Email' }}
        />
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          autoComplete="current-password"
          required
          inputProps={{ 'aria-label': 'Password' }}
        />
        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={isLoading} aria-label="Sign in">
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" component={RouterLink} to="/forgot-password" color="primary">
            Forgot password?
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}
